import Slider, { Settings } from 'react-slick';
import { useMemo, useRef, useState } from 'react';
import { SliderProps, SliderMethods } from './types/index';
import NewsWidget from './newsWidget';
import NewsModal from './newsModal.tsx';
import NewsFeedSliderContainer from './NewsFeedSliderContainer';
import { Box, useTheme } from '@mui/material';
import { Cog } from 'mdi-material-ui';
import { VigoTypography } from '../../../../@vigo/vigo-typography/index';
import Timer from './timer';

const NewsFeedSlider = ({
  loading = true,
  slideDuration,
  newsFeed,
  showTimer = true,
  showCountdown = true
}: SliderProps) => {
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(true);

  const theme = useTheme();
  const sliderRef = useRef<Slider | null>(null);

  const sliderSettings = useMemo<Settings>(() => ({
    variableWidth: true,
    arrows: false,
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 1000,
    cssEase: 'ease-in-out',
    afterChange: (current: number) => setActiveSlide(current),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 1, slidesToScroll: 1 } },
      { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  }), []);

  const sliderMethods: SliderMethods = {
    next: () => {
      if (sliderRef.current) {
        sliderRef.current.slickNext();
      }
    },
    previous: () => {
      if (sliderRef.current) {
        sliderRef.current.slickPrev();
      }
    }
  };

  const handleSliderMouseOver = (): void => {
    if (modalOpen) return;

    setPlaying(false);
  };

  const handleSliderMouseOut = (): void => {
    if (modalOpen) return;

    setPlaying(true);
  };

  const openNewsModal = () => {
    setModalOpen(true);
    setPlaying(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setPlaying(true);
  };

  const handleTimerComplete = () => {
    sliderMethods.next();

    return { shouldRepeat: true, delay: 1 };
  };

  if (loading || !newsFeed || newsFeed.length === 0) {
    return (
      <Box
        sx={{
          ...(theme.palette.mode === 'dark' && { border: `1px solid #1e1e1e`, borderRadius: '10px' }),
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
        }}>
        {newsFeed?.length === 0 && (
          <VigoTypography value="No articles available" sx={{ fontSize: '1.3rem' }} />
        )}
        {(loading || !newsFeed) && (
          <>
            <Cog
              htmlColor={theme.palette.mode === 'dark' ? '#1e1e1e' : theme.palette.primary.dark}
              sx={{
                fontSize: '5rem',
                animation: 'spin 3s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
            <VigoTypography value="Loading newsfeed articles ..." sx={{ fontSize: '1.3rem' }} />
          </>
        )}
      </Box>
    );
  }

  if (newsFeed.length === 1) {
    return (
      <>
        {newsFeed.map(feedItem => (
          <NewsWidget
            key={feedItem.title}
            newsItem={feedItem}
            onMouseOver={handleSliderMouseOver}
            onMouseOut={handleSliderMouseOut}
            openNewsModal={openNewsModal}
            singleItem
          />
        ))}
        <NewsModal open={modalOpen} handleClose={handleModalClose} newsFeed={newsFeed} activeIndex={activeSlide} />
      </>
    );
  }

  return (
    <>
      <NewsFeedSliderContainer>
        <Slider ref={sliderRef} {...sliderSettings}>
          {newsFeed.map(feedItem => (
            <NewsWidget
              key={`${feedItem.title}`}
              newsItem={feedItem}
              onMouseOver={handleSliderMouseOver}
              onMouseOut={handleSliderMouseOut}
              openNewsModal={openNewsModal}
              isActive={activeSlide === newsFeed.indexOf(feedItem)}
            />
          ))}
        </Slider>
        <Box sx={{ position: 'absolute', bottom: 5, right: 10 }}>
          {showTimer && (
            <Timer
              isPlaying={playing}
              duration={slideDuration}
              onComplete={handleTimerComplete}
              showCountdown={showCountdown}
            />
          )}
        </Box>
      </NewsFeedSliderContainer>

      <NewsModal open={modalOpen} handleClose={handleModalClose} newsFeed={newsFeed} activeIndex={activeSlide} aria-labelledby='news-modal-title' />
    </>
  );
};

export default NewsFeedSlider;
